import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Filter, 
  TrendingDown, 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Zap,
  Server,
  Database,
  Activity,
  BarChart3,
  RefreshCw,
  Eye
} from 'lucide-react';

const EventAnalysisNoiseReduction = () => {
  const [stats, setStats] = useState({
    total_events_received: 0,
    total_events_after_reduction: 0,
    duplicate_events_suppressed: 0,
    noise_reduction_percentage: 0,
    clusters_created: 0,
    root_causes_identified: 0,
    avg_cluster_size: 0,
    processing_time_ms: 0
  });
  
  const [clusters, setClusters] = useState([]);
  const [activeEvents, setActiveEvents] = useState([]);
  const [sapMetrics, setSapMetrics] = useState({});
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedView, setSelectedView] = useState('overview'); // overview, clusters, events, sap

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load all data in parallel
      const [statsRes, clustersRes, eventsRes, sapRes] = await Promise.all([
        fetch(`/api/event-analysis/stats`),
        fetch(`/api/event-analysis/clusters?limit=10`),
        fetch(`/api/event-analysis/events/active?limit=20`),
        fetch(`/api/event-analysis/sap/metrics`)
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats || {});
      }

      if (clustersRes.ok) {
        const clustersData = await clustersRes.json();
        setClusters(clustersData.clusters || []);
      }

      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setActiveEvents(eventsData.events || []);
      }

      if (sapRes.ok) {
        const sapData = await sapRes.json();
        setSapMetrics(sapData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    try {
      setAnalyzing(true);
      
      const response = await fetch(`/api/event-analysis/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        // Update data after analysis
        await loadData();
        alert(`Analysis completed! Noise reduced by ${result.stats?.noise_reduction_percentage?.toFixed(1) || 0}%`);
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      alert('Error running analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEventTypeIcon = (eventType) => {
    if (eventType?.includes('sap')) return '🔶';
    if (eventType?.includes('database')) return '🗃️';
    if (eventType?.includes('connectivity')) return '🔌';
    if (eventType?.includes('performance')) return '⚡';
    return '⚠️';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Event Analysis & Noise Reduction</h1>
          <p className="text-gray-600 mt-1">
            AI-powered event deduplication and root cause identification with SAP Focused Run integration
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={runAnalysis}
            disabled={analyzing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Zap className={`w-4 h-4 ${analyzing ? 'animate-pulse' : ''}`} />
            {analyzing ? 'Analyzing...' : 'Run Analysis'}
          </button>
        </div>
      </div>

      {/* View Selector */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { key: 'overview', label: 'Overview', icon: BarChart3 },
          { key: 'clusters', label: 'Event Clusters', icon: Filter },
          { key: 'events', label: 'Active Events', icon: AlertTriangle },
          { key: 'sap', label: 'SAP Metrics', icon: Server }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setSelectedView(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedView === key
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {selectedView === 'overview' && (
        <div className="space-y-6">
          {/* Key Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-blue-600">Total Events</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.total_events_received}</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-500 rounded-lg">
                  <TrendingDown className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-green-600">Noise Reduction</p>
                  <p className="text-2xl font-bold text-green-900">{stats.noise_reduction_percentage?.toFixed(1) || 0}%</p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Filter className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-purple-600">Clusters Created</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.clusters_created}</p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
              <div className="flex items-center">
                <div className="p-2 bg-orange-500 rounded-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-orange-600">Processing Time</p>
                  <p className="text-2xl font-bold text-orange-900">{stats.processing_time_ms?.toFixed(0) || 0}ms</p>
                </div>
              </div>
            </div>
          </div>

          {/* Noise Reduction Summary */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Noise Reduction Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">{stats.total_events_received}</div>
                <div className="text-sm text-gray-600">Events Received</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{stats.duplicate_events_suppressed}</div>
                <div className="text-sm text-gray-600">Duplicates Suppressed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{stats.total_events_after_reduction}</div>
                <div className="text-sm text-gray-600">Final Root Causes</div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Noise Reduction Efficiency</span>
                <span>{stats.noise_reduction_percentage?.toFixed(1) || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-red-500 to-green-500 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.min(stats.noise_reduction_percentage || 0, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Clusters Tab */}
      {selectedView === 'clusters' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Event Clusters</h3>
            <span className="text-sm text-gray-600">{clusters.length} clusters found</span>
          </div>
          
          <div className="space-y-4">
            {clusters.map((cluster, index) => (
              <div key={cluster.cluster_id} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      Cluster #{index + 1}
                    </span>
                    <span className="text-sm text-gray-600">
                      {cluster.total_events} events • {cluster.noise_reduction_percentage?.toFixed(1)}% reduction
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatTimestamp(cluster.created_at)}
                  </span>
                </div>

                {/* Root Cause Event */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200 mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-green-900">Root Cause Event</span>
                    <span className={`px-2 py-1 rounded text-xs ${getSeverityColor(cluster.root_cause_event?.severity)}`}>
                      {cluster.root_cause_event?.severity?.toUpperCase()}
                    </span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-1">
                    {getEventTypeIcon(cluster.root_cause_event?.event_type)} {cluster.root_cause_event?.title}
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">{cluster.root_cause_event?.description}</p>
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>Host: {cluster.root_cause_event?.host}</span>
                    <span>Service: {cluster.root_cause_event?.service}</span>
                    <span>Time: {formatTimestamp(cluster.root_cause_event?.timestamp)}</span>
                  </div>
                </div>

                {/* Duplicate Events Summary */}
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-4 h-4 text-red-600" />
                    <span className="font-medium text-red-900">Suppressed Duplicates</span>
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                      {cluster.duplicate_events?.length || 0} events
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Similar events from the same pattern have been suppressed to reduce noise.
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Events Tab */}
      {selectedView === 'events' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Active Events (Root Causes Only)</h3>
            <span className="text-sm text-gray-600">{activeEvents.length} active events</span>
          </div>
          
          <div className="space-y-3">
            {activeEvents.map((event, index) => (
              <div key={event.event_id} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(event.severity)}`}>
                      {event.severity?.toUpperCase()}
                    </span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                      {event.event_type?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatTimestamp(event.timestamp)}
                  </span>
                </div>
                
                <h4 className="font-medium text-gray-900 mb-1">
                  {getEventTypeIcon(event.event_type)} {event.title}
                </h4>
                <p className="text-sm text-gray-600 mb-3">{event.description}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-500">
                  <div><span className="font-medium">Host:</span> {event.host}</div>
                  <div><span className="font-medium">Service:</span> {event.service || 'N/A'}</div>
                  <div><span className="font-medium">Component:</span> {event.component || 'N/A'}</div>
                  <div><span className="font-medium">Source:</span> {event.source_system}</div>
                </div>
                
                {event.metrics && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-xs text-gray-600 mb-1">Metrics:</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(event.metrics).map(([key, value]) => (
                        <span key={key} className="bg-gray-100 px-2 py-1 rounded text-xs">
                          {key}: {typeof value === 'number' ? value.toFixed(1) : value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SAP Metrics Tab */}
      {selectedView === 'sap' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">SAP Focused Run Metrics</h3>
            <span className="text-sm text-gray-600">
              Last updated: {sapMetrics.last_updated ? new Date(sapMetrics.last_updated).toLocaleString() : 'N/A'}
            </span>
          </div>

          {/* SAP Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Server className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-blue-600">Total SAP Events</p>
                  <p className="text-2xl font-bold text-blue-900">{sapMetrics.total_sap_events || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 rounded-lg p-6 border border-red-200">
              <div className="flex items-center">
                <div className="p-2 bg-red-500 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-red-600">Critical Events</p>
                  <p className="text-2xl font-bold text-red-900">{sapMetrics.critical_events || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-500 rounded-lg">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-green-600">System Health</p>
                  <p className="text-2xl font-bold text-green-900">{sapMetrics.system_health_score || 0}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Event Types Breakdown */}
          {sapMetrics.event_types_breakdown && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">SAP Event Types</h4>
              <div className="space-y-3">
                {Object.entries(sapMetrics.event_types_breakdown).map(([type, count]) => (
                  <div key={type} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 capitalize">
                      {type.replace(/_/g, ' ')}
                    </span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${(count / Math.max(...Object.values(sapMetrics.event_types_breakdown))) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Components Breakdown */}
          {sapMetrics.components_breakdown && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Affected SAP Components</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(sapMetrics.components_breakdown).map(([component, count]) => (
                  <div key={component} className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{count}</div>
                    <div className="text-sm text-gray-600 capitalize">
                      {component.replace(/_/g, ' ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventAnalysisNoiseReduction;